/**
 * Accessibility testing utilities
 * @module @orchard9ai/testing-utils/utils/accessibility
 */

import { configureAxe, toHaveNoViolations } from 'jest-axe';
import axe from 'axe-core';

// Extend Jest matchers
expect.extend(toHaveNoViolations);

/**
 * Default axe configuration
 */
export const defaultAxeConfig = {
  rules: {
    // Disable region rule for test environments
    region: { enabled: false },
    // Ensure color contrast is checked
    'color-contrast': { enabled: true },
    // Check for valid ARIA attributes
    'aria-valid-attr': { enabled: true },
    'aria-valid-attr-value': { enabled: true },
    // Check for keyboard accessibility
    'keyboard-accessible': { enabled: true },
  },
};

/**
 * Configure axe for testing
 */
export const axeConfig = configureAxe(defaultAxeConfig);

/**
 * Check element for accessibility violations
 */
export async function checkA11y(
  element: Element | Document,
  options?: {
    rules?: Record<string, { enabled: boolean }>;
    runOnly?: string[];
    exclude?: string[];
  }
): Promise<axe.AxeResults> {
  const config = {
    ...defaultAxeConfig,
    ...options,
  };

  const results = await axe.run(element, config);
  return results;
}

/**
 * Assert no accessibility violations
 */
export async function expectNoA11yViolations(
  element: Element | Document,
  options?: Parameters<typeof checkA11y>[1]
): Promise<void> {
  const results = await checkA11y(element, options);

  if (results.violations.length > 0) {
    const violations = results.violations.map((violation) => ({
      id: violation.id,
      impact: violation.impact,
      description: violation.description,
      nodes: violation.nodes.length,
      help: violation.help,
    }));

    throw new Error(
      `Found ${results.violations.length} accessibility violations:\n${JSON.stringify(
        violations,
        null,
        2
      )}`
    );
  }
}

/**
 * Get accessibility tree
 */
export function getAccessibilityTree(element: Element): string {
  const tree: string[] = [];

  function traverse(node: Element, depth = 0): void {
    const indent = '  '.repeat(depth);
    const role = node.getAttribute('role') || node.tagName.toLowerCase();
    const label = node.getAttribute('aria-label') || node.textContent?.trim() || '';

    tree.push(`${indent}${role}: ${label}`);

    Array.from(node.children).forEach((child) => {
      traverse(child as Element, depth + 1);
    });
  }

  traverse(element);
  return tree.join('\n');
}

/**
 * Check keyboard navigation
 */
export function checkKeyboardNavigation(element: Element): {
  focusableElements: Element[];
  tabOrder: Element[];
  hasSkipLinks: boolean;
} {
  // Get all focusable elements
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const focusableElements = Array.from(element.querySelectorAll(focusableSelectors));

  // Get tab order
  const tabOrder = focusableElements
    .map((el) => ({
      element: el,
      tabIndex: parseInt(el.getAttribute('tabindex') || '0', 10),
    }))
    .sort((a, b) => {
      if (a.tabIndex === 0 && b.tabIndex === 0) return 0;
      if (a.tabIndex === 0) return 1;
      if (b.tabIndex === 0) return -1;
      return a.tabIndex - b.tabIndex;
    })
    .map((item) => item.element);

  // Check for skip links
  const skipLinks = element.querySelectorAll('a[href^="#"]');
  const hasSkipLinks = Array.from(skipLinks).some((link) => {
    const text = link.textContent?.toLowerCase() || '';
    return text.includes('skip') || text.includes('jump');
  });

  return {
    focusableElements,
    tabOrder,
    hasSkipLinks,
  };
}

/**
 * Check ARIA labels
 */
export function checkAriaLabels(element: Element): {
  unlabeledInputs: Element[];
  unlabeledButtons: Element[];
  missingAltTexts: Element[];
  invalidAria: Element[];
} {
  // Check for unlabeled inputs
  const inputs = Array.from(element.querySelectorAll('input, select, textarea'));
  const unlabeledInputs = inputs.filter((input) => {
    const id = input.getAttribute('id');
    const hasLabel = id && element.querySelector(`label[for="${id}"]`);
    const hasAriaLabel = input.getAttribute('aria-label');
    const hasAriaLabelledBy = input.getAttribute('aria-labelledby');

    return !hasLabel && !hasAriaLabel && !hasAriaLabelledBy;
  });

  // Check for unlabeled buttons
  const buttons = Array.from(element.querySelectorAll('button'));
  const unlabeledButtons = buttons.filter((button) => {
    const hasText = button.textContent?.trim();
    const hasAriaLabel = button.getAttribute('aria-label');
    const hasAriaLabelledBy = button.getAttribute('aria-labelledby');

    return !hasText && !hasAriaLabel && !hasAriaLabelledBy;
  });

  // Check for missing alt texts
  const images = Array.from(element.querySelectorAll('img'));
  const missingAltTexts = images.filter((img) => !img.hasAttribute('alt'));

  // Check for invalid ARIA attributes
  const elementsWithAria = Array.from(element.querySelectorAll('[aria-*]'));
  const invalidAria = elementsWithAria.filter((el) => {
    const attributes = Array.from(el.attributes).filter((attr) => attr.name.startsWith('aria-'));

    // Simple validation - in production, use full ARIA spec
    return attributes.some((attr) => {
      const validAttrs = [
        'aria-label',
        'aria-labelledby',
        'aria-describedby',
        'aria-hidden',
        'aria-live',
        'aria-atomic',
        'aria-relevant',
        'aria-busy',
        'aria-disabled',
        'aria-expanded',
        'aria-haspopup',
        'aria-pressed',
        'aria-selected',
        'aria-checked',
        'aria-current',
        'aria-invalid',
        'aria-required',
        'aria-readonly',
        'aria-valuemin',
        'aria-valuemax',
        'aria-valuenow',
        'aria-valuetext',
        'aria-orientation',
        'aria-level',
        'aria-posinset',
        'aria-setsize',
        'aria-colcount',
        'aria-colindex',
        'aria-colspan',
        'aria-rowcount',
        'aria-rowindex',
        'aria-rowspan',
        'aria-sort',
        'aria-multiline',
        'aria-multiselectable',
        'aria-placeholder',
        'aria-owns',
        'aria-flowto',
        'aria-controls',
        'aria-activedescendant',
        'aria-autocomplete',
        'aria-keyshortcuts',
        'aria-roledescription',
        'aria-modal',
        'aria-details',
        'aria-errormessage',
      ];

      return !validAttrs.includes(attr.name);
    });
  });

  return {
    unlabeledInputs,
    unlabeledButtons,
    missingAltTexts,
    invalidAria,
  };
}

/**
 * Assert focus is managed correctly
 */
export function assertFocusManagement(element: Element): void {
  const { focusableElements, hasSkipLinks } = checkKeyboardNavigation(element);

  if (focusableElements.length === 0) {
    throw new Error('No focusable elements found');
  }

  if (!hasSkipLinks && focusableElements.length > 5) {
    console.warn('Consider adding skip links for better keyboard navigation');
  }
}

/**
 * Assert proper ARIA usage
 */
export function assertAriaCompliance(element: Element): void {
  const { unlabeledInputs, unlabeledButtons, missingAltTexts, invalidAria } =
    checkAriaLabels(element);

  const errors: string[] = [];

  if (unlabeledInputs.length > 0) {
    errors.push(`Found ${unlabeledInputs.length} unlabeled input(s)`);
  }

  if (unlabeledButtons.length > 0) {
    errors.push(`Found ${unlabeledButtons.length} unlabeled button(s)`);
  }

  if (missingAltTexts.length > 0) {
    errors.push(`Found ${missingAltTexts.length} image(s) without alt text`);
  }

  if (invalidAria.length > 0) {
    errors.push(`Found ${invalidAria.length} element(s) with invalid ARIA attributes`);
  }

  if (errors.length > 0) {
    throw new Error(`ARIA compliance issues:\n${errors.join('\n')}`);
  }
}
